import * as React from 'react';
import { Calendar, PlusCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { type DateRange } from 'react-day-picker';

export interface DateRangeFilterProps {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  /**
   * `toolbar` matches the dashed, size-sm triggers of the data-table filter
   * rows: the placeholder stays visible as the filter's label and the selected
   * range is appended as a badge.
   */
  variant?: 'default' | 'inline' | 'toolbar';
}

export function DateRangeFilter({
  value,
  onChange,
  placeholder = 'Select date range',
  className,
  variant = 'default',
}: DateRangeFilterProps) {
  const [open, setOpen] = React.useState(false);
  /**
   * Range picking takes two clicks, so the first one is held here instead of
   * being pushed through `onChange`. react-day-picker completes the range on
   * the very first click (`{ from: day, to: day }`), which would otherwise
   * close the popover — and commit a one-day filter — before an end date can
   * be picked. `undefined` means "not mid-selection", i.e. show `value`.
   */
  const [draft, setDraft] = React.useState<DateRange | undefined>(undefined);

  const handleOpenChange = (nextOpen: boolean) => {
    // Abandon a half-finished range when the popover closes, so re-opening
    // starts from the committed value rather than a dangling start date.
    if (!nextOpen) setDraft(undefined);
    setOpen(nextOpen);
  };

  const handleSelect = (_range: DateRange | undefined, day: Date) => {
    if (!draft?.from) {
      setDraft({ from: day, to: undefined });
      return;
    }
    // Second click completes the range in either direction.
    const [from, to] = day < draft.from ? [day, draft.from] : [draft.from, day];
    setDraft(undefined);
    onChange({ from, to });
    setOpen(false);
  };

  const formatDateRange = (range: DateRange | undefined): string => {
    if (!range?.from) return '';
    if (!range.to) {
      return range.from.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    const fromStr = range.from.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const toStr = range.to.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${fromStr} - ${toStr}`;
  };

  const calendar = (
    <CalendarComponent
      initialFocus
      mode="range"
      defaultMonth={(draft ?? value)?.from}
      selected={draft ?? value}
      onSelect={handleSelect}
      numberOfMonths={2}
    />
  );

  if (variant === 'toolbar') {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn('tw:border-dashed', className)}
          >
            {!value?.from && <PlusCircle />}
            {placeholder}
            {value?.from && (
              <>
                <Separator
                  orientation="vertical"
                  className="tw:mx-2 tw:h-full"
                />
                <Badge
                  variant="secondary"
                  className="tw:rounded-sm tw:px-1 tw:font-normal"
                >
                  {formatDateRange(value)}
                </Badge>
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="tw:w-auto tw:p-0" align="start">
          {calendar}
        </PopoverContent>
      </Popover>
    );
  }

  if (variant === 'inline') {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'tw:text-sm tw:font-normal tw:text-neutral-900 tw:cursor-pointer tw:hover:text-neutral-700',
              !value && 'tw:text-neutral-500',
              className,
            )}
          >
            {value?.from ? formatDateRange(value) : <span>{placeholder}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="tw:w-auto tw:p-0" align="start">
          {calendar}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'tw:w-full tw:justify-start tw:text-left tw:font-normal',
            !value && 'tw:text-muted-foreground',
            className,
          )}
        >
          <Calendar className="tw:mr-2 tw:size-4" />
          {value?.from ? formatDateRange(value) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="tw:w-auto tw:p-0" align="start">
        {calendar}
      </PopoverContent>
    </Popover>
  );
}
